import { useEffect, useState } from 'react';
import { DocumentFileManager, type CuboneFile } from '../documents/DocumentFileManager';
import { DocumentReader } from '../documents/DocumentReader';
import { downloadDocumentFile, getDocumentNode, listDocumentSpaces } from '../../services/documents';
import type { DocumentNode, DocumentSpace } from '../../types/documents';
import { parseContentDispositionFilename, saveBlobAs } from '../../utils/downloadHelpers';
import { ContactActivityFiles } from './ContactActivityFiles';

interface Props { contactId: string; }

/** Adapter del tab contatto: risolve il dossier e delega tutta la UI condivisa. */
export function ContactFilesTab({ contactId }: Props) {
  const [space, setSpace] = useState<DocumentSpace | null>(null);
  const [currentFolder, setCurrentFolder] = useState<CuboneFile | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<CuboneFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [readerNode, setReaderNode] = useState<DocumentNode | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setCurrentFolder(null);
    setSelectedFiles([]);
    setReaderNode(null);
    listDocumentSpaces()
      .then((spaces) => {
        if (cancelled) return;
        const contactSpace = spaces.find((candidate) =>
          candidate.space_type === 'ENTITY'
          && candidate.owner_type === 'contact'
          && String(candidate.owner_id) === String(contactId));
        setSpace(contactSpace ?? null);
        setError(!contactSpace);
      })
      .catch((reason) => {
        if (cancelled) return;
        console.error('[ContactFilesTab] dossier load failed', reason);
        setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [contactId]);

  if (loading) {
    return <div className="py-8 text-center text-sm text-[var(--hexa-text-muted)]">Caricamento documenti...</div>;
  }
  if (error || !space) {
    return <><ContactActivityFiles contactId={contactId} /><div role="alert" className="py-8 text-center text-sm text-[var(--hexa-text-muted)]">Dossier documentale non disponibile.</div></>;
  }

  const downloadReaderNode = async () => {
    if (!readerNode) return;
    const response = await downloadDocumentFile(readerNode.id);
    saveBlobAs(response.data, parseContentDispositionFilename(response.headers?.['content-disposition']) || readerNode.name);
  };

  return <>
    <ContactActivityFiles contactId={contactId} />
    <h4 className="mb-2 text-xs font-semibold text-[var(--hexa-text-primary)]">File del contatto</h4>
    <DocumentFileManager
      space={space}
      currentFolder={currentFolder}
      selectedFiles={selectedFiles}
      capabilities={space.capabilities}
      mode="embedded"
      onFolderChange={setCurrentFolder}
      onSelectionChange={setSelectedFiles}
      onFileOpen={async (file) => { if (file.isDirectory) setCurrentFolder(file); else setReaderNode(await getDocumentNode(file._id)); }}
    />
    {readerNode && <DocumentReader node={readerNode} canDownload={space.capabilities.download} onClose={() => setReaderNode(null)} onDownload={downloadReaderNode} />}
  </>;
}
